import { useCallback, useEffect, useRef, useState } from 'react';

export const CAPTURE_LIMIT_SECONDS = 180;

// A session owns its recorder and callbacks, including pending microphone permission.
export default function useCapture(video = false) {
  const [status, setStatus] = useState('idle');
  const [url, setUrl] = useState(null);
  const [blob, setBlob] = useState(null);
  const [mimeType, setMimeType] = useState(null);
  const [error, setError] = useState(null);
  const [stream, setStream] = useState(null);
  const [seconds, setSeconds] = useState(0);
  const session = useRef(null);
  const urlRef = useRef(null);
  const alive = useRef(true);

  const release = useCallback(take => {
    clearInterval(take.clock);
    clearTimeout(take.limit);
    take.media?.getTracks().forEach(track => { track.onended = null; track.stop(); });
  }, []);

  const cancel = useCallback(() => {
    const take = session.current;
    session.current = null;
    if (take) {
      if (take.recorder?.state !== 'inactive') {
        try { take.recorder?.stop(); } catch { /* The device may already have stopped. */ }
      }
      release(take);
    }
    if (alive.current) { setStatus('idle'); setStream(null); setError(null); }
  }, [release]);

  const stop = useCallback(() => {
    const take = session.current;
    if (!take?.recorder || take.finishing) return;
    take.finishing = true;
    clearInterval(take.clock);
    clearTimeout(take.limit);
    if (alive.current) {
      setSeconds(Math.min(CAPTURE_LIMIT_SECONDS, Math.floor((Date.now() - take.started) / 1000)));
      setStatus('stopping');
      setStream(null);
    }
    try { if (take.recorder.state !== 'inactive') take.recorder.stop(); }
    catch {
      session.current = null;
      if (alive.current) { setError('device'); setStatus('error'); }
    }
    release(take);
  }, [release]);

  // Drops the finished take, so the next recording starts from nothing.
  const clear = useCallback(() => {
    cancel();
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    urlRef.current = null;
    setUrl(null); setBlob(null); setMimeType(null);
  }, [cancel]);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      cancel();
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    };
  }, [cancel]);

  const start = useCallback(async () => {
    if (session.current) return;
    const take = { chunks: [] };
    session.current = take;
    setStatus('requesting'); setError(null); setSeconds(0);
    const isCurrent = () => alive.current && session.current === take;
    const fail = reason => {
      if (!isCurrent()) return;
      cancel(); setError(reason); setStatus('error');
    };
    try {
      if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
        fail('unsupported'); return;
      }
      const media = await navigator.mediaDevices.getUserMedia({ audio: true, video: video ? { facingMode: 'user' } : false });
      take.media = media;
      if (!isCurrent()) { release(take); return; }
      const choices = video ? ['video/webm;codecs=vp9,opus', 'video/webm', 'video/mp4'] : ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
      const type = choices.find(value => window.MediaRecorder.isTypeSupported?.(value));
      const recorder = new window.MediaRecorder(media, type ? { mimeType: type } : undefined);
      take.recorder = recorder;
      recorder.ondataavailable = event => { if (isCurrent() && event.data.size) take.chunks.push(event.data); };
      recorder.onerror = () => fail('device');
      recorder.onstop = () => {
        release(take);
        if (!isCurrent()) return;
        const recorded = new Blob(take.chunks, { type: recorder.mimeType });
        if (!recorded.size) { fail('empty'); return; }
        try {
          const nextUrl = URL.createObjectURL(recorded);
          if (urlRef.current) URL.revokeObjectURL(urlRef.current);
          urlRef.current = nextUrl;
          setUrl(nextUrl); setBlob(recorded); setMimeType(recorder.mimeType); setStream(null); setStatus('ready');
          session.current = null;
        } catch { fail('device'); }
      };
      media.getTracks().forEach(track => { track.onended = () => fail('device'); });
      recorder.start(1000);
      take.started = Date.now();
      setStream(media); setStatus('recording');
      take.clock = setInterval(() => {
        if (isCurrent()) setSeconds(Math.min(CAPTURE_LIMIT_SECONDS, Math.floor((Date.now() - take.started) / 1000)));
      }, 250);
      take.limit = setTimeout(stop, CAPTURE_LIMIT_SECONDS * 1000);
    } catch (failure) {
      fail(['NotAllowedError', 'SecurityError'].includes(failure.name) ? 'permission' : failure.name === 'NotFoundError' ? 'missing' : 'device');
    }
  }, [cancel, release, stop, video]);

  return { start, stop, cancel, clear, status, recording: status === 'recording', starting: status === 'requesting', url, blob, error, stream, seconds, mimeType };
}
